import React, { useState } from 'react';
import {
  ImageBackground,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from '../components/ui/Button';
import Icon, { IconName } from '../components/ui/Icon';
import Text from '../components/ui/Text';
import PageHeader from '../components/account/PageHeader';
import RideBookingSheet from '../components/ride/RideBookingSheet';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../theme';

/**
 * "Book a Driver" - what the home card opens.
 *
 * Reached only from `DriverCard`. Driving is not in the service catalogue and
 * has no category tile, so this page is the whole of what anyone can find out
 * about it before booking: what it is, what is fixed about it, and the order
 * the four steps happen in.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE IS FETCHED
 * ---------------------------------------------------------------------------
 * The copy below - the assurances, the four steps - is constant in this file.
 * It is a description of the offer, not a rendering of one: there is no driver
 * catalogue entry and no price list on the API.
 *
 * `onBook` IS wired now. It opens `RideBookingSheet`, which collects a pickup
 * and a drop and posts to `POST /customers/me/rides`. What is still absent is
 * money: ride pricing does not exist server-side, so nothing on this page
 * quotes a fare and the booking carries none.
 */

/** The four assurances across the top of the card under the banner. */
const ASSURANCES: { icon: IconName; lines: [string, string] }[] = [
  { icon: 'user', lines: ['Experienced', '& Verified'] },
  { icon: 'shieldCheck', lines: ['Safe &', 'Reliable'] },
  { icon: 'clock', lines: ['On-time', 'Service'] },
  { icon: 'headset', lines: ['24x7', 'Support'] },
];

/**
 * The four steps, in order. The numbering is part of the label, as drawn.
 *
 * Title only - the one-line description each step used to carry is gone. Four
 * columns at a phone's width give each about 75pt, and a sentence in 75pt
 * wraps to three or four lines, which turned a glanceable strip into a wall.
 * The titles alone say the same thing at a glance.
 */
const STEPS: { icon: IconName; title: string }[] = [
  { icon: 'calendar', title: '1. Book' },
  { icon: 'user', title: '2. Driver Assigned' },
  { icon: 'pin', title: '3. Driver Arrives' },
  { icon: 'checkCircle', title: '4. Ride & Drop' },
];

/** The artwork's own 1600x849 - 1.88:1. */
const BANNER_RATIO = 1600 / 849;

/**
 * The most of the window's height the hero may take.
 *
 * The banner is full-bleed, so its height follows its width - and at a ratio
 * chosen for looks rather than for fit that height is unbounded: on a short
 * phone a 1.35:1 hero ate the top half of the page and pushed "How it works"
 * off the bottom. This caps it. Whichever is smaller, the artwork's own ratio
 * or 28% of the window, is what the hero gets, so the sections below it always
 * start above the fold.
 */
const BANNER_MAX_SCREEN_SHARE = 0.28;

type Props = {
  onBack: () => void;
  /**
   * Told the queued ride's id once a driver has been ordered.
   *
   * Optional because this page is complete without it - the booking succeeds
   * either way. It exists so whoever mounted this screen can leave it and show
   * the live ride, which lives on the Bookings tab rather than here.
   */
  onBooked?: (rideId: string) => void;
};

function DriverScreen({ onBack, onBooked }: Props) {
  /* The booking sheet is owned here rather than by the caller: the CTA that
     opens it is on this page, and a parent holding the flag would have to be
     told about a button it does not render. */
  const [booking, setBooking] = useState(false);
  const { sideInset } = useResponsive();
  /*
   * The bar is pinned to the bottom of the window, which on Android is behind
   * the gesture pill or the three-button nav - without this the button renders
   * underneath them and the bottom half of "Book Now" cannot be tapped.
   *
   * `SafeAreaView` above cannot do it: the screen is mounted with `edges` that
   * exclude `bottom`, and even with it the padding would land on the scroll
   * area rather than on the bar. Floored at `spacing.md` so the bar keeps its
   * own padding on hardware that reports no bottom inset at all.
   */
  const insets = useSafeAreaInsets();

  /*
   * The hero's height, bounded by the window rather than by the artwork.
   *
   * `width / BANNER_RATIO` is what the illustration wants at full bleed;
   * `height * BANNER_MAX_SCREEN_SHARE` is the most the page can spare. Taking
   * the smaller means a tall phone renders the artwork at its true proportions
   * and a short one crops it - instead of the page overflowing.
   */
  const { width: winWidth, height: winHeight } = useWindowDimensions();
  const bannerHeight = Math.min(
    winWidth / BANNER_RATIO,
    winHeight * BANNER_MAX_SCREEN_SHARE,
  );

  return (
    <View style={styles.root}>
      {/* `flush`, so the strip is the canvas colour and carries no bottom
          rule - the banner starts immediately under it and a white bar with a
          hairline would cut across the top of the artwork. */}
      <PageHeader title="Book a Driver" onBack={onBack} flush />

      {/*
        `sideInset` is NOT on the content container.

        On a wide screen it centres the page in a column - but applied here it
        would inset the hero too, and a full-bleed banner that stops short of
        the edges is just a card again. So the inset goes on each section
        below instead, and the banner alone spans the whole width.
      */}
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* ------------------------------------------------------- banner */}
        {/*
          The copy sits on the artwork rather than above it. `driver.png` is
          drawn with its whole left half empty for exactly this - the wheel,
          the route and the keys all live in the right two-thirds.
        */}
        <ImageBackground
          source={require('../../assets/driver/driver.png')}
          style={[styles.banner, { height: bannerHeight }]}
          imageStyle={styles.bannerImage}
          resizeMode="cover"
        >
          <View style={styles.bannerBody}>
            {/* No headline here - the page header directly above already
                says "Book a Driver", and repeating it 40pt lower was the same
                words twice in one glance. */}
            <Text variant="bodySm" color={colors.inkBody}>
              Professional driver at your service.
            </Text>
            <Text variant="bodySm" color={colors.inkBody}>
              We drive, so you can relax.
            </Text>
          </View>
        </ImageBackground>

        {/* --------------------------------------------------- assurances */}
        {/* One row of four, divided by hairlines rather than boxed
            separately - they are one claim in four parts, not four cards. */}
        <View style={[styles.assurances, { marginHorizontal: screenPadding + sideInset }]}>
          {ASSURANCES.map((item, index) => (
            <View
              key={item.lines.join(' ')}
              style={[
                styles.assurance,
                index > 0 ? styles.assuranceDivided : null,
              ]}
            >
              <Icon name={item.icon} size={22} color={colors.primary} />
              <View style={styles.assuranceCopy}>
                <Text variant="caption" align="center" color={colors.inkBody}>
                  {item.lines[0]}
                </Text>
                <Text variant="caption" align="center" color={colors.inkBody}>
                  {item.lines[1]}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* ------------------------------------------------------- about */}
        <View style={[styles.section, { marginHorizontal: screenPadding + sideInset }]}>
          <Text variant="title" color={colors.ink} style={styles.sectionTitle}>
            About this service
          </Text>
          <Text variant="body" color={colors.inkBody}>
            Our professional driver will reach your location, drive your vehicle
            safely and drop you along with your vehicle to your destination.
            Perfect for one-way trips, late night travel, office drop, and more.
          </Text>
        </View>

        {/* -------------------------------------------------- how it works */}
        <View style={[styles.section, { marginHorizontal: screenPadding + sideInset }]}>
          <Text variant="title" color={colors.ink} style={styles.sectionTitle}>
            How it works
          </Text>

          {/*
            Four across with arrows between, as the reference draws it.

            The labels are the constraint: at a phone's width each column gets
            roughly a quarter of the page, which is narrower than "2. Driver
            Assigned" can set on one line. So the columns wrap and every one is
            stretched to the tallest of them - `alignItems: 'stretch'` on the
            row - which keeps the four discs on one baseline instead of each
            floating in its own box.

            The arrows sit at a fixed width outside the flexed columns, so they
            take no space from the copy and stay pinned between the discs.
          */}
          <View style={styles.steps}>
            {STEPS.map((step, index) => (
              <React.Fragment key={step.title}>
                {index > 0 ? (
                  <View style={styles.stepArrow}>
                    <Icon
                      name="arrowRight"
                      size={14}
                      color={colors.inkMuted}
                    />
                  </View>
                ) : null}

                <View style={styles.step}>
                  <View style={styles.stepDisc}>
                    <Icon name={step.icon} size={20} color={colors.onPrimary} />
                  </View>
                  <Text
                    variant="labelSm"
                    align="center"
                    color={colors.ink}
                    style={styles.stepTitle}
                  >
                    {step.title}
                  </Text>
                </View>
              </React.Fragment>
            ))}
          </View>
        </View>
      </ScrollView>

      {/* ---------------------------------------------------------- CTA */}
      {/* Outside the `ScrollView`, so the price and the button are on screen
          at every scroll position - the same place `CartBar` sits on home. */}
      <View
        style={[
          styles.cta,
          {
            paddingHorizontal: screenPadding + sideInset,
            paddingBottom: Math.max(insets.bottom, spacing.md),
          },
        ]}
      >
        {/* No price. Ride pricing is not implemented server-side - there is
            no fare, no estimate and nothing that charges anybody - so this bar
            shows the action alone rather than a number invented in this file.
            The block that used to sit here, and the `PRICE_FROM` constant it
            read, are both gone rather than commented out: a hidden price is a
            price somebody re-enables without checking whether it became true. */}
        <Button
          label="Book Now"
          size="lg"
          onPress={() => setBooking(true)}
          style={styles.book}
        />
      </View>

      <RideBookingSheet
        visible={booking}
        onClose={() => setBooking(false)}
        onBooked={rideId => {
          setBooking(false);
          onBooked?.(rideId);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    paddingBottom: spacing.xxl,
  },

  /*
   * Full-bleed, not a card.
   *
   * No side margins and no top margin, so it runs edge to edge and butts
   * straight up under the header - which is why the header is `flush`: the two
   * share the canvas colour and read as one masthead rather than a strip above
   * a picture.
   *
   * Its height is computed in the component and capped against the window, so
   * the hero can never push the sections below it off the bottom of the page.
   */
  banner: {
    justifyContent: 'center',
    paddingHorizontal: screenPadding,
    /*
     * Pulled up into the header's bottom padding.
     *
     * The header already sits flush on the same canvas colour, so there was no
     * rule between them to close - what was left was 12pt of the header's own
     * `paddingBottom` reading as a gap above the artwork. Negative margin of
     * exactly that much lifts the banner into it, so the illustration starts
     * immediately under the title rather than a step below it. The title keeps
     * its own `paddingTop`, so nothing overlaps the text.
     */
    marginTop: -spacing.md,
  },
  /* Square. A radius here would round the top two corners against the header
     and the bottom two against the page, which is the card look this is
     deliberately not. */
  bannerImage: {
    borderRadius: 0,
  },
  bannerBody: {
    /* Wider than the card version's 60%: at full width the wheel sits further
       right in the frame, so the copy has more clear field to run into. */
    maxWidth: '66%',
    gap: 2,
  },
  /*
   * White, and lifted off the canvas rather than outlined.
   *
   * It was already `surface` - but `surface` is #FFFFFF against a #F5F7F2
   * page, and those two are close enough that a hairline between them reads
   * as no edge at all: on device the card looked like more banner. A soft
   * shadow separates them the way the near-equal tones cannot, so the outline
   * comes off - an outline AND a shadow is two answers to one question.
   */
  assurances: {
    flexDirection: 'row',
    /* `marginHorizontal` comes from the call site - it carries the wide-screen
       side inset, which this file's other blocks get the same way. */
    marginTop: spacing.lg,
    paddingVertical: spacing.lg,
    borderRadius: radii.xl,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  assurance: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  assuranceDivided: {
    borderLeftWidth: hairline.borderWidth,
    borderLeftColor: hairline.borderColor,
  },
  assuranceCopy: {
    alignItems: 'center',
  },

  section: {
    /* As `assurances` - the horizontal margin is applied at the call site. */
    marginTop: spacing.xxl,
    gap: spacing.sm,
  },
  sectionTitle: {
    fontWeight: '700',
  },

  steps: {
    flexDirection: 'row',
    /* Not `center`: the four columns wrap to different heights, and centring
       each one floats its disc at a different y. Stretched, they all start at
       the top and the discs share a baseline. */
    alignItems: 'stretch',
    marginTop: spacing.md,
  },
  step: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.sm,
  },
  /* Filled brand green, not the pale accent - so the four steps read as the
     page's one sequence of actions rather than as more soft chips. The glyph
     inverts to `onPrimary` with it; a green icon on green is invisible. */
  stepDisc: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepTitle: {
    marginTop: spacing.xs,
  },
  /*
   * Fixed width and outside the flexed columns, so the arrows never steal
   * width from copy that is already tight. The top padding drops it to the
   * discs' vertical middle - it points between them, not at the labels.
   */
  stepArrow: {
    width: spacing.lg,
    alignItems: 'center',
    paddingTop: spacing.lg,
  },

  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  book: {
    flex: 1,
  },
});

export default DriverScreen;
